/**
 * usePrayerTimes
 * React hook that fetches today's prayer times for the masjid from the free
 * Aladhan API, derives iqamah times from fixed offsets, and caches the result
 * in localStorage per day. The pure helpers (formatTime, addMinutes, date
 * formatters) are exported separately so they can be unit-tested without React.
 */
import { useEffect, useState } from 'react';
import { MASJID, PRAYER_CALCULATION_METHOD } from '../config/masjid';

export const PRAYER_ORDER = [
  'Fajr',
  'Sunrise',
  'Dhuhr',
  'Asr',
  'Maghrib',
  'Isha',
];

// Minutes added to the adhan (call to prayer) to get the iqamah (congregation)
// time. Sunrise is informational only and has no iqamah.
// TODO: Confirm these offsets with the masjid and adjust if the schedule changes.
export const IQAMAH_OFFSETS = {
  Fajr: 45,
  Dhuhr: 15,
  Asr: 15,
  Maghrib: 10,
  Isha: 15,
};

// Bump the version suffix to invalidate any previously cached (stale) results.
const CACHE_PREFIX = 'chmcc-prayer-times-v2';

export const pad2 = (n) => String(n).padStart(2, '0');

// Format a 24-hour "HH:MM" string into a friendly 12-hour time (e.g. "5:47 AM").
export const formatTime = (time24) => {
  if (!time24) return '';
  const [hourStr, minute] = time24.split(':');
  let hour = parseInt(hourStr, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${period}`;
};

// Add minutes to a 24-hour "HH:MM" string, returning a new "HH:MM" string.
export const addMinutes = (time24, minutesToAdd) => {
  if (!time24) return '';
  const [hourStr, minStr] = time24.split(':');
  const total =
    parseInt(hourStr, 10) * 60 + parseInt(minStr, 10) + minutesToAdd;
  const wrapped = ((total % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(wrapped / 60))}:${pad2(wrapped % 60)}`;
};

// Local calendar date as YYYY-MM-DD (used for the per-day cache key).
export const getCacheDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// The Aladhan /timings/{date} endpoint expects the date in DD-MM-YYYY format.
// Passing an ISO date (YYYY-MM-DD) is misparsed and returns the wrong year.
export const getApiDate = () => {
  const d = new Date();
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
};

const readCache = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors (private mode, quota, etc.)
  }
};

/**
 * Fetches today's prayer times for the masjid from the free Aladhan API.
 * Returns, for each prayer, the adhan time and the iqamah time
 * (adhan + a fixed offset). Results are cached in localStorage per day.
 */
const usePrayerTimes = () => {
  // Hydrate synchronously from cache (if present) so we never flash a loading
  // state for a same-day revisit and avoid setState-in-effect churn.
  const [state, setState] = useState(() => {
    const cached = readCache(`${CACHE_PREFIX}-${getCacheDate()}`);
    if (cached) return { ...cached, loading: false, error: null };
    return {
      timings: null,
      gregorianDate: '',
      hijriDate: '',
      loading: true,
      error: null,
    };
  });

  useEffect(() => {
    const cacheKey = `${CACHE_PREFIX}-${getCacheDate()}`;
    let cancelled = false;

    // Already hydrated from cache above — nothing to fetch.
    if (readCache(cacheKey)) return;

    const { latitude, longitude } = MASJID.location;
    const url = `https://api.aladhan.com/v1/timings/${getApiDate()}?latitude=${latitude}&longitude=${longitude}&method=${PRAYER_CALCULATION_METHOD}`;

    const controller = new AbortController();

    fetch(url, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        const data = json?.data;
        if (!data?.timings) throw new Error('Unexpected API response');

        const timings = PRAYER_ORDER.map((name) => {
          const adhan24 = (data.timings[name] || '').slice(0, 5);
          const offset = IQAMAH_OFFSETS[name];
          const iqamah24 = offset != null ? addMinutes(adhan24, offset) : null;
          return {
            name,
            adhan: formatTime(adhan24),
            iqamah: iqamah24 ? formatTime(iqamah24) : null,
          };
        });

        const result = {
          timings,
          gregorianDate: data.date?.readable || '',
          hijriDate: data.date?.hijri
            ? `${data.date.hijri.day} ${data.date.hijri.month?.en} ${data.date.hijri.year} AH`
            : '',
        };

        writeCache(cacheKey, result);
        setState({ ...result, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled || err.name === 'AbortError') return;
        setState((prev) => ({ ...prev, loading: false, error: err.message }));
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return state;
};

export default usePrayerTimes;
